import React, { useCallback, useMemo } from 'react';
import * as yup from 'yup';
import moment from 'moment';
import { useStringsContext } from '../../../../../contexts/strings';
import svStrings from '../../../../../sv-strings';
import { PlayerPollKeys, Poll } from '../../types';
import PollCard from '../poll-card';
import TextInputWithValidate, {
  InputWithValidateProps,
} from '../../../../common/input/text-input-with-validate';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DateTimeWithValidate from '../../../../common/input/datetime-with-validate';
import { SxStyle } from '../../../../../types';

const timeValidation = yup
  .date()
  .min(moment(), svStrings.validations.validDateInPast)
  .required(svStrings.validations.isRequired)
  .typeError(svStrings.validations.validDate);

const timeValidationEditing = yup
  .date()
  .required(svStrings.validations.isRequired)
  .typeError(svStrings.validations.validDate);

const pollNameKey: PlayerPollKeys = 'name';
const startTimeKey: PlayerPollKeys = 'startTime';
const endTimeKey: PlayerPollKeys = 'endTime';

const styles: SxStyle = {
  inputTitle: {
    display: 'block',
    marginBottom: '1px',
  },
};

interface Props extends InputWithValidateProps {
  selectedPoll: Poll | null;
  title: string;
  helperText: string;
  label: string;
  titleStartEnd: string;
  isEditing: boolean;
}

const PollNameTimeCard = ({
  selectedPoll,
  title,
  label,
  helperText,
  titleStartEnd,
  onValue,
  onValidation,
  hideValidationError,
  isEditing,
}: Props) => {
  const { strings } = useStringsContext();

  const startTimeValue = useMemo(() => {
    return selectedPoll?.[startTimeKey]
      ? moment(selectedPoll?.[startTimeKey]).format('YYYY-MM-DD HH:mm')
      : '';
  }, [selectedPoll]);

  const endTimeValue = useMemo(() => {
    return selectedPoll?.[endTimeKey]
      ? moment(selectedPoll?.[endTimeKey]).format('YYYY-MM-DD HH:mm')
      : '';
  }, [selectedPoll]);

  const handleValueTime = useCallback(
    (key: string, value: string | null) => {
      onValue && onValue(key, moment(value).toISOString());

      // TODO Fix set endtime based on starttime
      // Set end time to start time + 1 hour (if it's not already set)
      // if (fieldKey === 'startTime' && !endTimeField.value) {
      //   endTimeField.value = moment(value).add(1, 'hours').format('YYYY-MM-DD HH:mm');
      // }
    },
    [onValue]
  );

  return (
    <PollCard title={title}>
      <TextInputWithValidate
        name={pollNameKey}
        validation={yup.string().required(strings.validations.isRequired)}
        label={label}
        value={selectedPoll?.[pollNameKey]}
        onValue={onValue}
        onValidation={onValidation}
        hideValidationError={hideValidationError}
        helperText={helperText}
      />
      <Stack spacing={2}>
        <Typography sx={styles.inputTitle} variant="body2-bold">
          {titleStartEnd}
        </Typography>
        <DateTimeWithValidate
          name={startTimeKey}
          validation={isEditing ? timeValidationEditing : timeValidation}
          label={strings.polls.startTimeLabel}
          value={startTimeValue}
          onValue={handleValueTime}
          onValidation={onValidation}
          hideValidationError={hideValidationError}
        />
        <DateTimeWithValidate
          name={endTimeKey}
          validation={isEditing ? timeValidationEditing : timeValidation}
          label={strings.polls.endTimeLabel}
          value={endTimeValue}
          onValue={handleValueTime}
          onValidation={onValidation}
          hideValidationError={hideValidationError}
        />
      </Stack>
    </PollCard>
  );
};

export default PollNameTimeCard;
